import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { IReuniao } from '../../../commons/types/crm';
import { useReuniaoActions } from '../../../states/reunioes/reuniao.actions';
import { createTheme, ThemeProvider } from '@mui/material';
import { Box, Button, Card, CardContent, Chip, Divider, Grid, IconButton, InputAdornment, Pagination, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { esES, ptBR } from '@mui/material/locale';
import { DataGrid } from '@mui/x-data-grid';
import { GridColDef } from '@mui/x-data-grid';
import { StatusReuniaoEnum } from '../../../commons/types/crm';
import moment from 'moment';
import { useTranslation } from '../../../commons/useTranslasion';
import { useDateFormatters } from '../../../utils/useDateFormatters';
import { IDrawerConcluir } from './DrawerReuniaoInterna';
import { forcarTimezone, handleStatus } from '../../Dashboard/TableReunioes';
import { AcoesColumn } from '../../Dashboard/acoesColumn';

const ReunioesAssessorados: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [reunioes, setReunioes] = useState<IReuniao[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);
  const reuniaoActions = useReuniaoActions();
  const [reuniao, setReuniao] = useState<IReuniao>();
  const { translate, language } = useTranslation();
  const { formatarData } = useDateFormatters();
  const lang = language ?? "pt-br";
  let locale = ptBR;
  switch (lang.slice(0, 2)) {
    case "es":
      locale = esES
      break;
  }

    const columns: (args: IDrawerConcluir) => GridColDef[] = (
      args: IDrawerConcluir,
    ) => {
      return [
        {
          field: "nomeAssessorado",
          headerName: translate("assessorado"),
          width: 130,
          flex: 130,
        },
        {
          field: "nomeEtapa",
          headerName: translate("etapa"),
          width: 120,
          flex: 120,
        },
        {
          field: "data",
          headerName: translate("data"),
          width: 100,
          flex: 100,
          valueGetter(value: any, row: IReuniao) {
            const reuniao = row as IReuniao;
            if (
              reuniao.status === StatusReuniaoEnum.PENDENTE_APROVACAO ||
              reuniao.status === StatusReuniaoEnum.APROVADA
            )
              return formatarData(reuniao.dtRealizadaInicio);
            return formatarData(reuniao.dtPrevista);
          },
          // Extrai a data real (timestamp) para ordenação correta
          sortComparator: (v1, v2, param1, param2) => {
            const row1 = param1.api.getRow(param1.id) as IReuniao;
            const row2 = param2.api.getRow(param2.id) as IReuniao;
            
            const date1 = row1.status === StatusReuniaoEnum.PENDENTE_APROVACAO || 
                         row1.status === StatusReuniaoEnum.APROVADA
                ? new Date(row1.dtRealizadaInicio!).getTime()
                : new Date(row1.dtPrevista!).getTime();
                
            const date2 = row2.status === StatusReuniaoEnum.PENDENTE_APROVACAO || 
                         row2.status === StatusReuniaoEnum.APROVADA
                ? new Date(row2.dtRealizadaInicio!).getTime()
                : new Date(row2.dtPrevista!).getTime();
                
            return date1 - date2;
          },
        },
        {
          field: "horaInicio",
          headerName: translate("inicio"),
          width: 100,
          flex: 100,
          valueGetter(params: any, row: IReuniao) {
            const reuniao = row;
            return moment(forcarTimezone(reuniao.dtRealizadaInicio!.toString()).toDate().toString()).format('HH:mm');
          },
        },
        {
          field: "horaFim",
          headerName: translate("fim"),
          width: 100,
          flex: 100,
          valueGetter(params: any, row: IReuniao) {
            const reuniao = row;
            return moment(forcarTimezone(reuniao.dtRealizadaFim!.toString()).toDate().toString()).format('HH:mm');
          },
        },
        {
          field: "status",
          headerName: translate("status"),
          width: 120,
          flex: 120,
          valueGetter(params: any, row: IReuniao) {
            const reuniao = row;
            return handleStatus(reuniao.status, reuniao.statusValidacao, translate);
          },
        },
        {
          field: "",
          headerName: translate("acoes"),
          width: 80,
          flex: 80,
          editable: false,
          renderCell: (params) => (
            <AcoesColumn
              row={params.row}
              setOpen={args.setOpen}
              setReuniao={args.setReuniao!}
            />
          ),
        },
      ];
    };

  useEffect(() => {
    reuniaoActions
      .list()
      .then((resp: any) => {
        resp.resultado = resp.resultado.filter((r: IReuniao) => r.ativo);
        setReunioes(resp.resultado)
      })
      .catch((e) => toast.error(e))
      .finally(() => setLoading(false));
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filtro, setFiltro] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const itensPorPagina = 10; // Quantidade de cards por página
  
  // Filtragem de reuniões com base no texto digitado
  const reunioesFiltradas = useMemo(() => {
    if (!filtro.trim()) return reunioes;
    
    const termoLower = filtro.toLowerCase();
    return reunioes.filter(reuniao => 
      reuniao.nomeAssessorado?.toLowerCase().includes(termoLower) ||
      reuniao.nomeEtapa?.toLowerCase().includes(termoLower) ||
      handleStatus(reuniao.status, reuniao.statusValidacao, translate)?.toLowerCase().includes(termoLower)
    );
  }, [reunioes, filtro]);
  
  // Cálculo para paginação
  const totalPaginas = Math.ceil(reunioesFiltradas.length / itensPorPagina);
  const reunioesPaginadas = useMemo(() => {
    // Se a opção "mostrar todos" estiver ativada, retorna todos os itens
    if (mostrarTodos) {
      return reunioesFiltradas;
    }
    
    // Caso contrário, aplica a paginação normal
    const inicio = (paginaAtual - 1) * itensPorPagina;
    const fim = inicio + itensPorPagina;
    return reunioesFiltradas.slice(inicio, fim);
  }, [reunioesFiltradas, paginaAtual, itensPorPagina, mostrarTodos]);
  
  // Resetar página quando o filtro muda ou quando alterna entre mostrar todos/paginar
  useEffect(() => {
    setPaginaAtual(1);
  }, [filtro, mostrarTodos]);
  
  // Função para trocar de página
  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPaginaAtual(value);
    // Rolar a página para o topo com animação suave
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Função para renderizar os cards em visualização móvel
  const renderMobileCards = () => {
    return (
      <Box sx={{ p: 1 }}>
        {/* Campo de busca para filtragem */}
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder={translate('buscar')}
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          sx={{ 
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        
        {reunioesFiltradas.length === 0 && (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
               {translate('carregando')}
            </Typography>
          </Box>
        )}
        
        {reunioesPaginadas.map((reuniao) => {
          // Formatação de data e hora
          const data = reuniao.status === StatusReuniaoEnum.PENDENTE_APROVACAO || 
                      reuniao.status === StatusReuniaoEnum.APROVADA
            ? formatarData(reuniao.dtRealizadaInicio)
            : formatarData(reuniao.dtPrevista);
          
          const horaInicio = moment(forcarTimezone(reuniao.dtRealizadaInicio!.toString()).toDate().toString()).format('HH:mm');
          const horaFim = moment(forcarTimezone(reuniao.dtRealizadaFim!.toString()).toDate().toString()).format('HH:mm');
          
          // Determinar cor do status para o chip
          // Status sempre com cor primary, padronizado para facilitar whitelabel
          const statusColor = 'primary';
          
          return (
            <Card 
              key={reuniao.id} 
              sx={{ 
                mb: 2, 
                borderRadius: '10px',
                boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
              }}
            >
              <CardContent sx={{ px: 1.5, py: 1.5 }}>
                {/* Cabeçalho do card com nome do assessorado */}
                <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold', mb: 1, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {reuniao.nomeAssessorado}
                </Typography>

                {/* Status em chip */}
                <Box sx={{ mb: 1.5 }}>
                  <Chip 
                    size="small" 
                    label={handleStatus(reuniao.status, reuniao.statusValidacao, translate)}
                    color={statusColor as any}
                    sx={{ 
                      fontSize: '0.75rem',
                      maxWidth: '100%',
                      height: 'auto',
                      '& .MuiChip-label': { 
                        whiteSpace: 'normal',
                        overflow: 'hidden',
                        padding: '4px 8px'
                      } 
                    }}
                  />
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                {/* Etapa - ocupa linha inteira para evitar corte */}
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ fontWeight: 'bold' }}>
                    {translate("etapa")}
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                    {reuniao.nomeEtapa}
                  </Typography>
                </Box>
                
                {/* Detalhes em colunas */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  {/* Coluna 1 */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ fontWeight: 'bold' }}>
                      {translate("data")}
                    </Typography>
                    <Typography variant="body2" component="div">
                      {data}
                    </Typography>
                  </Box>
                  
                  {/* Coluna 2 */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ fontWeight: 'bold' }}>
                      {translate("inicio")}/{translate("fim")}
                    </Typography>
                    <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
                      {horaInicio} - {horaFim}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Área de ações */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <AcoesColumn
                    row={reuniao}
                    setOpen={setOpen}
                    setReuniao={setReuniao!}
                  />
                </Box>
              </CardContent>
            </Card>
          );
        })}
        
        {/* Paginação e opção de mostrar todos */}
        {(totalPaginas > 1 || mostrarTodos) && (
          <Stack spacing={1} sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
            {!mostrarTodos && (
              <Pagination 
                count={totalPaginas} 
                page={paginaAtual}
                onChange={handleChangePage}
                color="primary"
                size="medium"
                siblingCount={0}
              />
            )}
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', maxWidth: '400px', mt: 1 }}>
              {!mostrarTodos ? (
                <Typography variant="caption" color="text.secondary">
                  {translate('pagina')} {paginaAtual} {translate('de')} {totalPaginas}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {translate('exibindo')} {reunioesFiltradas.length} {translate('itens')}
                </Typography>
              )}
              
              <Button 
                size="small" 
                color="primary" 
                variant="text"
                onClick={() => setMostrarTodos(!mostrarTodos)}
                sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
              >
                {mostrarTodos ? translate('mostrarPaginado') : translate('mostrarTodos')}
              </Button>
            </Box>
          </Stack>
        )}
      </Box>
    );
  };

  return (
    <ThemeProvider theme={createTheme({}, locale)}>
      {isMobile ? (
        renderMobileCards()
      ) : (
        <DataGrid
          rows={reunioes}
          loading={loading}
          columns={columns({
            open,
            setOpen,
            loadingSave,
            setLoadingSave,
            setReuniao,
            setReunioes,
          })}
          initialState={{
            sorting: {
              sortModel: [{ field: 'data', sort: 'desc' }],
            },
            pagination: {
              paginationModel: { pageSize: 50, page: 0 },
            }
          }}
          sx={{
            borderRadius: '10px',
            minHeight: '700px',
            height: 'auto',
          }}
        />
      )}
    </ThemeProvider>
  );
};

export default ReunioesAssessorados;
